import {
  DermloopAssessmentTypeEnum,
  MedicalRecordFieldEnum,
  ConditionType,
  UrgencyRating,
  YesNoUnknownEnum,
} from '@dermloop/static-values';
import {
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  ValidateNested,
} from 'class-validator';
import { GenderDto } from './clinical-details';

export type ValueLookup = {
  kind: 'lookup';
  id: string;
  text: string;
  otherChoice?: string;
};

export type ValueYesNoUnknown = {
  kind: 'yesNoUnknown';
  yesNoUnknown: YesNoUnknownEnum;
  text: string;
  hasSpecifiedValue?: boolean;
};

export type ValueMultichoice = {
  kind: 'multichoice';
  yesNoUnknown: YesNoUnknownEnum;
  ids?: string[];
  otherChoice?: string;
  text: string;
  age?: string;
};

/**
 * A row of selections with no yes/no gate in front of them. Distinct from
 * `multichoice`, whose choices are the yes branch of a gate the reader answered:
 * an ungated row has no such answer, and stating one would invent it.
 */
export type ValueOptions = {
  kind: 'options';
  ids?: string[];
  text: string;
};

export type ValueText = {
  kind: 'text';
  text: string;
};

export type ValueUrgencyRating = {
  kind: 'urgencyRating';
  urgencyRating: UrgencyRating;
  text: string;
};

export type Value =
  | ValueLookup
  | ValueYesNoUnknown
  | ValueMultichoice
  | ValueOptions
  | ValueText
  | ValueUrgencyRating;

export type RecordEntryDto = {
  key: string;
  value: Value;
  field?: MedicalRecordFieldEnum;
};

export type HistoryItemDto = {
  isoDate: string;
  title: string;
  entries: RecordEntryDto[];
  cautionText?: string;
  assessmentType: DermloopAssessmentTypeEnum;
  timeSpentReviewing?: number;
  /** Answered through a form template rather than the legacy fixed fields. */
  formDriven?: boolean;
  /**
   * The latest completion a specialist answer stands for, when later edits to
   * it changed nothing the record states and so are not listed.
   */
  answeredUntil?: string;
  /** A form-driven case's first triage; its other entries are copied under their own title. */
  firstRegistration?: boolean;
};

export type MedicalRecordEntryDto = {
  entries: RecordEntryDto[];
  cautionText?: string;
};

export type MedicalRecordConditionTypeDto = {
  type: ConditionType;
  name: string;
};

export type MedicalRecordDto = {
  conditionType: MedicalRecordConditionTypeDto;
  /** @deprecated Use conditionType */
  skinConditionType?: MedicalRecordConditionTypeDto;
  history: HistoryItemDto[];
};

export type CopyMedicalRecordDto = {
  text: string;
};

export class CopyMedicalRecordReqDto {
  @ValidateNested()
  medicalRecord: MedicalRecordDto;

  @IsOptional()
  @IsString()
  ssn?: string;

  @IsString()
  url: string;

  @ValidateNested()
  @IsOptional()
  gender?: GenderDto;
}

export class CopyDraftReqDto {
  @IsUUID()
  triageId: string;

  @IsOptional()
  @IsString()
  ssn?: string;

  @IsNotEmpty()
  @IsString()
  url: string;
}
